import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { useMediaQuery } from '@/hooks/useMediaQuery';

const STORAGE_KEY = 'sidebarCollapsed';

/**
 * The band where the sidebar stands in the way.
 *
 * Below 768 it is an off-canvas drawer and takes no room at all; from 1024 up
 * there is room for it next to a page. In between, its 272px is a third of the
 * viewport, and the pages that lay out in columns are left with a strip: the
 * inbox gave a conversation 185px at 768, which is not enough for a sender
 * name and a timestamp side by side.
 */
const TABLET_MIN = '(min-width: 768px)';
const TABLET_MAX = '(max-width: 1023px)';

const storedCollapsed = () => localStorage.getItem(STORAGE_KEY) === 'true';

const inTabletBandNow = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(TABLET_MIN).matches && window.matchMedia(TABLET_MAX).matches;
};

interface SidebarContextType {
  isCollapsed: boolean;
  toggle: () => void;
  expand: () => void;
  collapse: () => void;
  /** Off-canvas drawer state, used only on phone-sized viewports. */
  isMobileOpen: boolean;
  openMobile: () => void;
  closeMobile: () => void;
  toggleMobile: () => void;
  /** Global search modal, triggered from the sidebar (as in the SP web app). */
  isSearchOpen: boolean;
  openSearch: () => void;
  closeSearch: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const SidebarProvider = ({ children }: { children: ReactNode }) => {
  // Collapsed on a tablet whatever the stored choice says, so the rail — which
  // loses the labels and nothing else — is what the client meets there.
  const [isCollapsed, setIsCollapsed] = useState(() => storedCollapsed() || inTabletBandNow());
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Two queries rather than one compound string, each in its own call: `&&`
  // would short-circuit the second hook away on the renders where the first
  // is false.
  const isAtLeastTablet = useMediaQuery(TABLET_MIN);
  const isBelowDesktop = useMediaQuery(TABLET_MAX);
  const isTabletBand = isAtLeastTablet && isBelowDesktop;

  // Crossing into the band collapses it; crossing out gives the client their
  // own choice back. Only a crossing does anything — the first render already
  // settled it, and a toggle made inside the band has to survive the renders
  // that follow. That toggle is simply not written down, so a tablet session
  // cannot decide how the sidebar opens on a desktop.
  const appliedBand = useRef(isTabletBand);

  useEffect(() => {
    if (appliedBand.current === isTabletBand) return;
    appliedBand.current = isTabletBand;
    setIsCollapsed(isTabletBand || storedCollapsed());
  }, [isTabletBand]);

  const remember = useCallback(
    (next: boolean) => {
      if (isTabletBand) return;
      localStorage.setItem(STORAGE_KEY, String(next));
    },
    [isTabletBand]
  );

  const toggle = useCallback(() => {
    setIsCollapsed(prev => {
      const next = !prev;
      remember(next);
      return next;
    });
  }, [remember]);

  const expand = useCallback(() => {
    setIsCollapsed(false);
    remember(false);
  }, [remember]);

  const collapse = useCallback(() => {
    setIsCollapsed(true);
    remember(true);
  }, [remember]);

  const openMobile = useCallback(() => setIsMobileOpen(true), []);
  const closeMobile = useCallback(() => setIsMobileOpen(false), []);
  const toggleMobile = useCallback(() => setIsMobileOpen(prev => !prev), []);

  const openSearch = useCallback(() => {
    setIsSearchOpen(true);
    setIsMobileOpen(false);
  }, []);
  const closeSearch = useCallback(() => setIsSearchOpen(false), []);

  return (
    <SidebarContext.Provider
      value={{
        isCollapsed,
        toggle,
        expand,
        collapse,
        isMobileOpen,
        openMobile,
        closeMobile,
        toggleMobile,
        isSearchOpen,
        openSearch,
        closeSearch,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};
